<template>
  <div class="formgrid grid w-12 md:w-6 pb-4">
    <div class="col-12">
      <div class="flex justify-content-between align-items-center mb-2">
        <p class="text-sm font-light pr-3">
          Selecciona y configura los aprobadores que revisarán y aprobarán las
          cartas generadas. Puedes agregar hasta
          {{ maxItems }} aprobadores.
        </p>
        <Button
          :label="`Agregar aprobador (máx. ${maxItems})`"
          icon="pi pi-plus"
          severity="secondary"
          @click="addApprover"
          :disabled="isAddDisabled"
        />
      </div>

      <div
        v-for="(approverDetail, index) in approversList"
        :key="index"
        class="surface-card border-round-md border-1 border-200 p-3 mt-3"
      >
        <div class="flex justify-content-between align-items-center mb-3">
          <span class="font-medium">Aprobador #{{ index + 1 }}</span>
          <div class="flex align-items-center">
            <Button
              v-if="approversList.length > 1"
              icon="pi pi-times"
              severity="danger"
              text
              rounded
              size="small"
              @click="requestRemoveApprover(index, $event)"
              :disabled="disabled"
            />
            <ConfirmPopup />
          </div>
        </div>

        <div class="formgrid grid">
          <div class="field col-12 md:col-6 flex flex-column">
            <label :for="`approver-select-${index}`">
              Seleccionar aprobador <span class="text-red-600">*</span>
            </label>
            <Dropdown
              :id="`approver-select-${index}`"
              :modelValue="approverDetail.approverId"
              :options="approversOptions"
              optionLabel="name"
              optionValue="id"
              filter
              @update:modelValue="(value) => handleApproverSelection(index, value as string)"
              :disabled="disabled"
            />
          </div>

          <div class="field col-12 md:col-6 flex flex-column">
            <label :for="`approver-name-${index}`">
              Nombre del aprobador <span class="text-red-600">*</span>
            </label>
            <InputText
              :id="`approver-name-${index}`"
              :modelValue="approverDetail.approverName"
              @update:modelValue="
                (value) => updateApproverField(index, 'approverName', value)
              "
              type="text"
              :disabled="disabled"
            />
          </div>

          <div class="field col-12 md:col-6 flex flex-column">
            <label :for="`approver-email-${index}`">Correo electrónico</label>
            <InputText
              :id="`approver-email-${index}`"
              :modelValue="approverDetail.approverEmail"
              @update:modelValue="
                (value) => updateApproverField(index, 'approverEmail', value)
              "
              type="email"
              :disabled="disabled"
            />
          </div>

          <div class="field col-12 md:col-6 flex flex-column">
            <label :for="`approver-phone-${index}`">Teléfono</label>
            <InputText
              :id="`approver-phone-${index}`"
              :modelValue="approverDetail.approverPhone"
              @update:modelValue="
                (value) => updateApproverField(index, 'approverPhone', value)
              "
              type="tel"
              :disabled="disabled"
            />
          </div>

          <div class="field col-12 md:col-6 flex flex-column">
            <label :for="`approver-position-${index}`">Cargo</label>
            <InputText
              :id="`approver-position-${index}`"
              :modelValue="approverDetail.approverPosition"
              @update:modelValue="
                (value) => updateApproverField(index, 'approverPosition', value)
              "
              type="text"
              :disabled="disabled"
            />
          </div>

          <div class="field col-12 md:col-6 flex flex-column">
            <label :for="`approver-signature-${index}`">Firma (Imagen)</label>
            <FileUpload
              :id="`approver-signature-${index}`"
              mode="basic"
              :disabled="disabled"
              class="font-medium w-6 text-center"
              chooseLabel="Subir firma"
              accept="image/*"
              style="background: #f9f9f9; border: #e4e4e4; color: gray"
              @select="(event) => handleSignatureSelected(index, event)"
              @clear="() => clearSignature(index)"
            />
            <div v-if="approverDetail.signatureUrl" class="mt-2 w-12">
              <div
                class="overflow-hidden border-1 border-200 surface-card p-2 border-round"
              >
                <img
                  :src="resolveSignatureUrl(approverDetail.signatureUrl)"
                  alt="Previsualización de firma"
                  class="block w-full"
                  style="max-height: 140px; object-fit: contain"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <small id="approvers-error" class="p-error">
        {{ errorMessage || '\u00a0' }}
      </small>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue'
  import { useConfirm } from 'primevue/useconfirm'
  import { FileUploadSelectEvent } from 'primevue/fileupload'
  import Approver from '@/modules/approvers/models/Approver'
  import { ILetterApproverDetail } from '../services/dtos/ILetterApproverDetail'

  interface Props {
    modelValue: ILetterApproverDetail[]
    approversOptions: Approver[]
    maxItems?: number
    disabled?: boolean
    errorMessage?: string | null
  }

  const props = defineProps<Props>()
  const emit = defineEmits<{
    (event: 'update:modelValue', value: ILetterApproverDetail[]): void
  }>()

  const confirm = useConfirm()
  const maxItems = computed(() => props.maxItems ?? 2)
  const disabled = computed(() => props.disabled ?? false)
  const errorMessage = computed(() => props.errorMessage ?? null)

  const cloneApprovers = (items: ILetterApproverDetail[] = []) =>
    items.map((item) => ({ ...item }))

  const createEmptyApprover = (): ILetterApproverDetail => ({
    approverId: '',
    approverName: '',
    approverPosition: null,
    approverPhone: null,
    approverEmail: null,
    signature: undefined,
    signatureUrl: null,
    isDeleted: false,
  })

  const approversList = ref<ILetterApproverDetail[]>([])
  let syncingFromParent = false

  watch(
    () => props.modelValue,
    async (newValue) => {
      syncingFromParent = true
      approversList.value = cloneApprovers(newValue ?? [])
      await nextTick()
      syncingFromParent = false
    },
    { immediate: true, deep: true }
  )

  watch(
    approversList,
    (value) => {
      if (syncingFromParent) return
      emit('update:modelValue', cloneApprovers(value))
    },
    { deep: true }
  )

  const isAddDisabled = computed(
    () => disabled.value || approversList.value.length >= maxItems.value
  )

  const addApprover = () => {
    if (isAddDisabled.value) return
    approversList.value = [...approversList.value, createEmptyApprover()]
  }

  const requestRemoveApprover = (index: number, event: Event) => {
    confirm.require({
      target: event.currentTarget as HTMLElement,
      message: '¿Estás seguro de que deseas eliminar este aprobador?',
      icon: 'pi pi-exclamation-circle',
      acceptLabel: 'Aceptar',
      rejectLabel: 'Cancelar',
      acceptClass: 'p-button-text p-button-danger font-light text-sm',
      rejectClass: 'p-button-primary font-light text-sm',
      accept: () => {
        const next = [...approversList.value]
        const approver = next[index]
        if (
          approver?.signatureUrl &&
          approver.signatureUrl.startsWith('blob:')
        ) {
          URL.revokeObjectURL(approver.signatureUrl)
        }
        next.splice(index, 1)
        approversList.value = next
      },
    })
  }

  const handleApproverSelection = (index: number, approverId: string) => {
    const next = [...approversList.value]
    const entry = next[index]
    if (!entry) return

    entry.approverId = approverId
    const selected = props.approversOptions?.find(
      (item) => item.id === approverId
    )
    entry.approverName = selected?.name ?? ''
    approversList.value = next
  }

  const updateApproverField = (
    index: number,
    field: keyof ILetterApproverDetail,
    value: any
  ) => {
    const next = [...approversList.value]
    const entry = next[index]
    if (!entry) return

    next[index] = { ...entry, [field]: value }
    approversList.value = next
  }

  const handleSignatureSelected = (
    index: number,
    event: FileUploadSelectEvent
  ) => {
    const file = event.files?.[0] as File | undefined
    if (!file) return

    const next = [...approversList.value]
    const entry = next[index]
    if (!entry) return

    if (entry.signatureUrl && entry.signatureUrl.startsWith('blob:')) {
      URL.revokeObjectURL(entry.signatureUrl)
    }

    entry.signature = file
    entry.signatureUrl = URL.createObjectURL(file)
    approversList.value = next
  }

  const clearSignature = (index: number) => {
    const next = [...approversList.value]
    const entry = next[index]
    if (!entry) return

    if (entry.signatureUrl && entry.signatureUrl.startsWith('blob:')) {
      URL.revokeObjectURL(entry.signatureUrl)
    }

    entry.signature = undefined
    entry.signatureUrl = null
    approversList.value = next
  }

  const getSignatureLabel = (signature: ILetterApproverDetail['signature']) => {
    if (!signature) return ''
    return typeof signature === 'string' ? signature : signature.name
  }

  const resolveSignatureUrl = (signatureUrl: string | null) => {
    if (!signatureUrl) return ''
    return signatureUrl
  }
</script>
